import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { CalendarClock, Mail, Loader2, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

/**
 * Admin automation settings (Session 36, P3 items f + g):
 *  - Monthly payout auto-release (toggle, minimum amount, day-of-month)
 *  - Daily admin briefing email (toggle, recipient, send-now preview)
 */
export default function AdminAutomationSettings() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [auto, setAuto] = useState({ enabled: false, minimum_amount: 25, day_of_month: 1 });
  const [brief, setBrief] = useState({ enabled: false, recipient: '' });
  const [savingAuto, setSavingAuto] = useState(false);
  const [savingBrief, setSavingBrief] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/payouts/auto-release/settings`, { headers }),
        axios.get(`${API}/admin/daily-briefing/settings`, { headers }),
      ]);
      setAuto(a.data);
      setBrief(b.data);
    } catch (err) {
      // non-fatal
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveAuto = async () => {
    setSavingAuto(true);
    try {
      const res = await axios.post(`${API}/admin/payouts/auto-release/settings`, auto, { headers });
      setAuto(res.data);
      toast.success('Auto-release settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSavingAuto(false);
    }
  };

  const saveBrief = async () => {
    setSavingBrief(true);
    try {
      const res = await axios.post(`${API}/admin/daily-briefing/settings`, brief, { headers });
      setBrief(res.data);
      toast.success('Daily briefing settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSavingBrief(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/daily-briefing/send-now`, {}, { headers });
      if (res.data.sent) toast.success(`Briefing sent to ${res.data.recipient}`);
      else toast.warning('Briefing could not be emailed (check SMTP), but metrics were generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="admin-automation-settings">
      {/* Monthly payout auto-release */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-gray-100 flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-indigo-300" /> Monthly Payout Auto-Release
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm text-gray-300">Enable monthly auto-release</Label>
            <Switch
              checked={auto.enabled}
              onCheckedChange={(v) => setAuto({ ...auto, enabled: v })}
              data-testid="auto-release-toggle"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-400">Minimum amount ($)</Label>
              <Input
                type="number" min="0" step="1"
                value={auto.minimum_amount}
                onChange={(e) => setAuto({ ...auto, minimum_amount: parseFloat(e.target.value) || 0 })}
                className="bg-white/5 border-white/10 mt-1"
                data-testid="auto-release-minimum"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-400">Day of month (1–28)</Label>
              <Input
                type="number" min="1" max="28" step="1"
                value={auto.day_of_month}
                onChange={(e) => setAuto({ ...auto, day_of_month: parseInt(e.target.value) || 1 })}
                className="bg-white/5 border-white/10 mt-1"
                data-testid="auto-release-day"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500">
            When enabled, eligible approved/pending payouts are auto-released via Stripe each month to recipients whose per-account total meets the minimum.
          </p>
          <Button
            size="sm" onClick={saveAuto} disabled={savingAuto}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="save-auto-release-btn"
          >
            {savingAuto ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Save
          </Button>
        </CardContent>
      </Card>

      {/* Daily briefing email */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-gray-100 flex items-center gap-2">
            <Mail className="w-4 h-4 text-cyan-300" /> Daily Briefing Email
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm text-gray-300">Send a daily digest</Label>
            <Switch
              checked={brief.enabled}
              onCheckedChange={(v) => setBrief({ ...brief, enabled: v })}
              data-testid="daily-briefing-toggle"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-400">Recipient email</Label>
            <Input
              type="email"
              value={brief.recipient || ''}
              onChange={(e) => setBrief({ ...brief, recipient: e.target.value })}
              className="bg-white/5 border-white/10 mt-1"
              data-testid="daily-briefing-recipient"
            />
          </div>
          <p className="text-xs text-gray-500">
            A once-a-day summary of new signups, sales, bookings, and what needs attention (pending payouts, approvals, workshop reviews).
          </p>
          <div className="flex gap-2">
            <Button
              size="sm" onClick={saveBrief} disabled={savingBrief}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="save-daily-briefing-btn"
            >
              {savingBrief ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Save
            </Button>
            <Button
              size="sm" variant="outline" onClick={sendNow} disabled={sending}
              className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
              data-testid="send-briefing-now-btn"
            >
              {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />} Send Now
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
